/**
 * The System of Seven — a date scale along a line
 *
 * Sāmudrika / Hasta Rekhā tradition, Pt II "Time — the System of Seven", pp. 142–143
 * and Plate XXIII: "Every line on the hand can be divided into sections giving
 * dates with more or less accuracy. The most important lines … are those of life
 * and fate … I have divided the line of fate into three great divisions, namely,
 * twenty-one, thirty-five, and forty-nine."
 *
 * ⚠ Two caveats are the Sāmudrika tradition's own, and travel with every result (`caveats`):
 *   1. dates come "with more or less accuracy";
 *   2. "the student must notice the class or type of hand before proceeding" —
 *      the scale is to be reduced or increased "in accordance with the length of
 *      the palm".
 *
 * ⚠ **Boundary.** The scale stops at 49. It is a way to place *events* on a line
 * the tradition uses that way; it is never a clock to the line's end. In
 * particular the marks along the Jīvana Rekhā are spaced by a fixed section
 * length in palm units, **not** by dividing the traced line into a fixed number
 * of parts — a short or broken line carries fewer marks, and the code does not
 * (and must not) turn "fewer marks" into any statement about a life.
 *
 * The section lengths and the head/heart anchor positions are engineering priors
 * read off Plate XXIII, not measured on real palms.
 *
 * © Manish Jagdish Thatte
 */

import { toPalmSpace } from './hand-geometry.js';
import { pointAt, lengthOf } from './polyline.js';

export const SOURCE = 'Sāmudrika / Hasta Rekhā tradition, Pt II, “Time — the System of Seven”, pp. 142–143, Plate XXIII';
export const MAX_AGE = 49;
export const STEP = 7;
export const CAVEATS = Object.freeze(['accuracy', 'hand-type-first']);

/** Default palm-space v of the head and heart crossings on the fate line (Plate XXIII); replaced by the traced lines when known. */
export const DEFAULT_ANCHORS = Object.freeze({ head: 0.5, heart: 0.8, saturn: 1.0 });
/** Palm-width units per seven-year section of the life line (prior). */
export const LIFE_SECTION = 0.11;

function orient(points, frame, lowFirst) {
  const a = toPalmSpace(points[0], frame); const b = toPalmSpace(points[points.length - 1], frame);
  const aLow = a.v <= b.v;
  return lowFirst === aLow ? points : points.slice().reverse();
}

/** Piecewise-linear v → age through the anchors (0,0) (head,21) (heart,35) (saturn,49). */
export function fateAge(v, anchors = DEFAULT_ANCHORS) {
  const k = [[0, 0], [anchors.head, 21], [anchors.heart, 35], [anchors.saturn, 49]];
  if (v <= 0) return 0;
  for (let i = 1; i < k.length; i++) if (v <= k[i][0]) return k[i - 1][1] + (k[i][1] - k[i - 1][1]) * (v - k[i - 1][0]) / (k[i][0] - k[i - 1][0]);
  return MAX_AGE;
}

/**
 * Marks on the fate line at 7, 14, … 49 where the line reaches them.
 * `anchors` may carry `head`/`heart` as the palm-space v at which those lines cross the fate line.
 */
export function fateMarks(points, frame, anchors = {}) {
  const A = { ...DEFAULT_ANCHORS, ...anchors };
  const pts = orient(points, frame, true);                              // wrist end first
  const v = pts.map((p) => toPalmSpace(p, frame).v);
  const marks = [];
  for (let age = STEP; age <= MAX_AGE; age += STEP) {
    for (let i = 1; i < pts.length; i++) {
      const a0 = fateAge(v[i - 1], A); const a1 = fateAge(v[i], A);
      if (a0 <= age && age <= a1 && a1 > a0) {
        const f = (age - a0) / (a1 - a0);
        marks.push({ age, point: [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * f, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * f], major: age === 21 || age === 35 || age === 49 });
        break;
      }
    }
  }
  return marks;
}

/** Marks on the life line: age 0 at its upper end, one section every `LIFE_SECTION` palm widths (× palm aspect), to 49 at most. */
export function lifeMarks(points, frame, { section = LIFE_SECTION } = {}) {
  const pts = orient(points, frame, false);                             // upper (index-side) end first
  const aspect = frame.length / frame.width;
  const pxPerSection = section * frame.width * aspect;
  const total = lengthOf(pts); const marks = [];
  for (let age = STEP; age <= MAX_AGE; age += STEP) {
    const d = (age / STEP) * pxPerSection;
    if (d > total) break;                                               // the line simply has no more marks — not a statement about anything
    marks.push({ age, point: pointAt(pts, d / total), major: false });
  }
  return marks;
}

/**
 * Date scale for a classified line.
 * `line` is `'fate'` or `'life'`; other lines return no marks (the Sāmudrika tradition allows them but gives no anchors).
 * Returns `{ line, marks, caveats, source, maxAge }`.
 */
export function dateScale(points, line, frame, opts = {}) {
  let marks = [];
  if (line === 'fate') marks = fateMarks(points, frame, opts.anchors);
  else if (line === 'life') marks = lifeMarks(points, frame, opts);
  return { line, marks, caveats: [...CAVEATS], source: SOURCE, maxAge: MAX_AGE };
}
